<template>
  <div
    class="cal-event"
    :class="[
      `cal-event--${variant}`,
      {
        'cal-event--all-day': event.allDay,
        'cal-event--dragging': isDragging,
        'cal-event--draggable': isDraggable,
        'cal-event--compact': compact
      },
      event.className
    ]"
    :style="[colorStyle, event.style ?? {}]"
    v-bind="passThrough('event')"
    :data-event-id="event.id"
    role="button"
    tabindex="0"
    @pointerdown="emit('pointerdown', $event)"
    @pointerenter="emit('pointerenter', $event)"
    @pointerleave="emit('pointerleave', $event)"
    @click="emit('click', $event)"
    @dblclick.stop="emit('dblclick', $event)"
    @keydown.enter.prevent="emit('click', $event)"
    @keydown.space.prevent="emit('click', $event)"
  >
    <!-- Hosts can replace the chip's contents while keeping drag, focus, and positioning -->
    <slot :event="event">
      <!-- Timeline chips use a solid tinted block; month chips use a small dot -->
      <span
        v-if="variant === 'dot'"
        class="cal-event__dot"
      />
      <span
        v-else
        class="cal-event__bar"
      />

      <Icon
        v-if="event.icon"
        :type="event.icon"
        class="cal-event__icon"
      />

      <span
        class="cal-event__title"
        v-bind="passThrough('eventTitle')"
      >
        {{ event.title }}
      </span>

      <span
        v-if="timeLabel"
        class="cal-event__time"
        v-bind="passThrough('eventTime')"
      >
        {{ timeLabel }}
      </span>
    </slot>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

// components
import Icon from '@/components/icon/Icon.vue'

// types
import type { CalendarEvent } from '@/types/components/calendar'

// utilities
import { resolveCalendarColor, tintCalendarColor } from '@/utilities/calendar/color'
import { formatEventTime } from '@/utilities/calendar/format'
import { useCalendarPassThrough } from '@/utilities/calendar/passthrough'

const props = withDefaults(
  defineProps<{
    /** Event this chip represents */
    event: CalendarEvent
    /** 'dot' for month cells and agendas, 'block' for the time grid */
    variant?: 'dot' | 'block'
    /** Whether this chip is the one currently being dragged */
    isDragging?: boolean
    /** Whether the chip may be dragged, used only for the cursor affordance */
    isDraggable?: boolean
    /** Hides the time and tightens spacing, used on narrow screens */
    compact?: boolean
    /** Show the end time alongside the start */
    showEndTime?: boolean
    /** Use 12-hour clock labels */
    hour12?: boolean
    /** Calendar-level fallback timezone */
    timezone?: string
  }>(),
  {
    variant: 'dot',
    isDragging: false,
    isDraggable: false,
    compact: false,
    showEndTime: false,
    hour12: false,
    timezone: undefined
  }
)

const emit = defineEmits<{
  click: [payload: MouseEvent | KeyboardEvent]
  dblclick: [payload: MouseEvent]
  pointerdown: [payload: PointerEvent]
  pointerenter: [payload: PointerEvent]
  pointerleave: [payload: PointerEvent]
}>()

const passThrough = useCalendarPassThrough()

/** Resolved accent colour, shared by the dot, bar, and tinted background */
const accentColor = computed(() => resolveCalendarColor(props.event.color))

/**
 * Colour custom properties consumed by the chip's stylesheet.
 * Block chips get a tinted surface so overlapping events stay readable.
 */
const colorStyle = computed(() => ({
  '--cal-event-accent': accentColor.value,
  '--cal-event-surface': tintCalendarColor(accentColor.value, 14)
}))

/** Time shown beside the title on timeline chips, omitted when the chip is too short */
const timeLabel = computed(() => {
  // Timeline position already encodes the start; short chips keep the title readable
  if (props.compact) {
    return ''
  }

  return formatEventTime(props.event, {
    hour12: props.hour12,
    showEnd: props.showEndTime,
    timezone: props.timezone
  })
})
</script>

<style scoped>
.cal-event {
  display: flex;
  align-items: center;
  gap: var(--cal-event-gap, 0.25rem);
  padding: var(--cal-event-padding, 0.125rem 0.25rem);
  border-radius: var(--cal-event-radius, var(--rounded-sm));
  font-size: var(--cal-event-font-size, calc(var(--text-sm) - 1px));
  line-height: 1.35;
  color: var(--text-primary);
  cursor: pointer;
  overflow: hidden;
  /* Long-press to drag must not select text or pop the iOS callout */
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
  transition:
    background-color 0.12s ease,
    opacity 0.12s ease;
}

.cal-event--draggable {
  cursor: grab;
}

.cal-event:hover {
  background-color: var(--bg-primary_hover);
}

.cal-event:focus-visible {
  outline: 2px solid var(--border-brand);
  outline-offset: -1px;
}

.cal-event--dragging {
  opacity: 0.35;
}

.cal-event__dot {
  flex-shrink: 0;
  width: 0.5rem;
  height: 0.5rem;
  border-radius: var(--rounded-full);
  background-color: var(--cal-event-accent);
}

.cal-event__icon {
  flex-shrink: 0;
  font-size: 0.7rem;
  color: var(--cal-event-accent);
}

.cal-event__time {
  flex-shrink: 0;
  color: var(--text-tertiary);
  font-variant-numeric: tabular-nums;
}

.cal-event__title {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 500;
}

/* Block variant: a filled surface with a coloured leading edge, as used in the time grid */
.cal-event--block {
  position: relative;
  align-items: baseline;
  flex-direction: row;
  flex-wrap: wrap;
  column-gap: 0.25rem;
  row-gap: 0;
  height: 100%;
  padding: var(--cal-event-padding, 0.1875rem 0.375rem);
  padding-left: 0.5rem;
  background-color: var(--cal-event-surface);
  border-radius: var(--cal-event-radius, var(--rounded-md));
}

.cal-event--block:hover {
  background-color: color-mix(in srgb, var(--cal-event-accent) 24%, transparent);
}

.cal-event--block .cal-event__bar {
  position: absolute;
  top: 0.1875rem;
  bottom: 0.1875rem;
  left: 0.1875rem;
  width: 0.1875rem;
  border-radius: var(--rounded-full);
  background-color: var(--cal-event-accent);
}

.cal-event--block .cal-event__title {
  /* Fill the row so the time sits on the far right; wrap the time when this hits 100% */
  flex: 1 0 auto;
  max-width: 100%;
  min-width: 0;
}

.cal-event--block .cal-event__time {
  flex: 0 0 auto;
  margin-left: auto;
  font-size: 0.6875rem;
}

/* Very short events hide the time and keep the title on one line */
.cal-event--block.cal-event--compact {
  flex-wrap: nowrap;
  align-items: center;
}

/* All-day chips read as a solid pill, matching Google's treatment */
.cal-event--all-day {
  background-color: var(--cal-event-surface);
}

.cal-event--all-day .cal-event__dot {
  width: 0.375rem;
  height: 0.375rem;
}
</style>
